// Client mirror of the server's quote-funnel classification (Session AAV, Loop 1).
//
// The analytics dashboard's counts are computed server-side (server/src/modules/quotes/
// quoteAnalytics.ts); its segments deep-link to the quotes list, whose filters run
// client-side over the list DTO. Both sides MUST classify a quote identically or a clicked
// count and the resulting list disagree, so the predicates here mirror the server's exactly
// and quoteFunnel.parity.test.ts asserts they agree (the same read-only-mirror pattern as
// the surcharge zone lists in quotePricing.ts).
//
// A row needs only status + the openedAt/sentAt stamps the list DTO already carries.

export interface FunnelRow {
  status: string;
  openedAt?: string | null;
  sentAt?: string | null;
}

// Statuses only reachable AFTER a quote was issued to the customer (mirror of
// SENT_EVIDENT_STATUSES). lost/voided are absent: a never-sent draft can be marked lost
// or voided directly, so those rows need explicit send evidence.
export const SENT_EVIDENT_STATUSES_CLIENT = new Set(["pending", "sent", "opened", "accepted", "signed", "expired", "declined"]);

// The quote was issued to the customer (mirror of quoteWasSent).
export function quoteWasSentClient(q: FunnelRow): boolean {
  return (
    q.sentAt != null ||
    SENT_EVIDENT_STATUSES_CLIENT.has(q.status) ||
    ((q.status === "lost" || q.status === "voided") && q.openedAt != null)
  );
}

// The customer opened the accept link (mirror of quoteWasOpened): the openedAt stamp, or a
// status that can only be reached from the accept page (a resend clears the stamp).
export function quoteWasOpenedClient(q: FunnelRow): boolean {
  return q.openedAt != null || q.status === "accepted" || q.status === "signed" || q.status === "declined" || q.status === "opened";
}

// The quote reached acceptance (mirror of quoteReachedAccepted).
export function quoteReachedAcceptedClient(status: string): boolean {
  return status === "accepted" || status === "signed";
}

// ---- Display helpers shared by the analytics page and the quotes-list banners ----

// "2026-06" -> "Jun 2026" without relying on the runtime's ICU month names, so the list
// banner reads exactly like the analytics trend row that was clicked.
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function monthLabel(month: string): string {
  const [y, m] = month.split("-");
  const idx = Number(m) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS_SHORT[idx]} ${y}` : month;
}

// Plain-English lift-type labels, mirroring the server's LIFT_TYPE_LABELS (liftSpec.ts)
// verbatim -- parity-tested -- so a clicked analytics row ("Ceiling hoist") and the list
// banner it opens use the same words. Falls back to the raw key for an unknown value.
export const LIFT_TYPE_LABELS_CLIENT: Record<string, string> = {
  passengerTrall: "Passenger traction MRL",
  passengerTramr: "Passenger traction, machine room",
  passengerHydr: "Passenger hydraulic",
  goodsTraction: "Goods traction",
  goodsHydr: "Goods hydraulic",
  goodsTractionMrl: "Goods traction MRL",
  platform: "Platform / disabled access",
  stepLift: "Step lift",
  throughFloor: "Through-floor (home lift)",
  serviceLift: "Service lift / laundry lift",
  trolleyLift: "Trolley lift",
  dumbwaiter: "Dumbwaiter",
  stairlift: "Stairlift (straight)",
  stairliftCurved: "Stairlift (curved)",
  scissorSmall: "Scissor lift (kitchen/small)",
  scissorGoods: "Scissor lift (goods)",
  heavyDuty: "Heavy duty / car lift",
  custom: "Custom / Other",
};
export function liftTypeDisplayLabel(key: string): string {
  return LIFT_TYPE_LABELS_CLIENT[key] ?? key;
}

// ---- The quote pipeline's stages (Session ASH, Q-7 and Q-9) ----
// Mirror of server/src/modules/quotes/quotePipeline.ts, bound by quoteFunnel.parity.test.ts, so the pipeline strip
// above the Quotes list and the analytics page's project segment use exactly the server's stages and words.

export type PipelineStageClient = "drafting" | "with_customer" | "won" | "lost" | "closed";

export const PIPELINE_STAGES_CLIENT: readonly PipelineStageClient[] = ["drafting", "with_customer", "won", "lost", "closed"];

export const PIPELINE_STAGE_LABELS_CLIENT: Record<PipelineStageClient, string> = {
  drafting: "Drafting",
  with_customer: "With the customer",
  won: "Won",
  lost: "Lost",
  closed: "Closed",
};

// Every QuoteStatus onto a stage (mirror of QUOTE_STATUS_STAGE). `accepted` is still with the customer: the
// agreement is out for signature and nothing is won until it is signed.
export const QUOTE_STATUS_STAGE_CLIENT: Record<string, PipelineStageClient> = {
  draft: "drafting",
  pending: "with_customer",
  sent: "with_customer",
  opened: "with_customer",
  accepted: "with_customer",
  signed: "won",
  declined: "lost",
  lost: "lost",
  voided: "closed",
  expired: "closed",
};

export function quoteStatusStageClient(status: string): PipelineStageClient {
  return QUOTE_STATUS_STAGE_CLIENT[status] ?? "closed";
}
